import React, { useState } from 'react';
import { useParent } from '../context/ParentContext';
import ParentEmptyState from '../components/states/ParentEmptyState';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import './ParentActivity.css';

const COLORS = ['#4f6ef7', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6'];

function ParentActivity() {
  const { currentActivity, selectedStudent, demoState } = useParent();
  const [dateRange, setDateRange] = useState('7d');

  if (demoState === 'empty' || !currentActivity.weeklyData) {
    return (
      <div className="parent-activity">
        <h1 className="parent-page-title">Activity</h1>
        <ParentEmptyState variant="no-activity" />
      </div>
    );
  }

  return (
    <div className="parent-activity">
      <div className="parent-activity__header">
        <h1 className="parent-page-title">{selectedStudent?.firstName}'s Study Activity</h1>
        <div className="parent-activity__date-range">
          {['7d', '30d', 'semester'].map(range => (
            <button
              key={range}
              className={`parent-activity__range-btn ${dateRange === range ? 'parent-activity__range-btn--active' : ''}`}
              onClick={() => setDateRange(range)}
            >
              {range === '7d' ? 'Last 7 Days' : range === '30d' ? 'Last 30 Days' : 'Semester'}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="parent-activity__metrics">
        <MetricBox label="Active Days" value={currentActivity.activeDays} unit="/7" />
        <MetricBox label="Study Sessions" value={currentActivity.studySessions} />
        <MetricBox label="Quizzes Completed" value={currentActivity.quizzesCompleted} />
        <MetricBox label="Avg Quiz Score" value={`${currentActivity.averageQuizScore}%`} />
        <MetricBox label="Flashcard Sessions" value={currentActivity.flashcardSessions} />
        <MetricBox label="Flashcards Reviewed" value={currentActivity.flashcardsReviewed} />
        <MetricBox label="Notes Created" value={currentActivity.notesCreated} />
        <MetricBox label="Study Conversations" value={currentActivity.studyConversations} />
        <MetricBox label="Documents Reviewed" value={currentActivity.documentsReviewed} />
        <MetricBox label="Assignments Completed" value={currentActivity.assignmentsCompleted} />
      </div>

      {/* Charts */}
      <div className="parent-activity__charts">
        {/* Activity by Day */}
        <div className="parent-activity__chart-card">
          <h3>Study Minutes by Day</h3>
          <div className="parent-activity__chart-container" role="img" aria-label="Bar chart showing study minutes per day">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={currentActivity.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="minutes" fill="#4f6ef7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity by Course */}
        <div className="parent-activity__chart-card">
          <h3>Time by Course</h3>
          <div className="parent-activity__chart-container" role="img" aria-label="Pie chart showing study time distribution by course">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={currentActivity.byCourse} dataKey="minutes" nameKey="course" cx="50%" cy="50%" outerRadius={80} label={({ course, percentage }) => `${percentage}%`}>
                  {currentActivity.byCourse.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="parent-activity__legend">
              {currentActivity.byCourse.map((item, idx) => (
                <span key={item.course} className="parent-activity__legend-item">
                  <span className="parent-activity__legend-dot" style={{ background: COLORS[idx % COLORS.length] }} />
                  {item.course}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Tool Usage */}
        <div className="parent-activity__chart-card">
          <h3>Tool Usage</h3>
          <div className="parent-activity__chart-container" role="img" aria-label="Bar chart showing tool usage counts">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={currentActivity.byTool} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="tool" tick={{ fontSize: 12 }} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly Engagement Trend */}
        <div className="parent-activity__chart-card">
          <h3>Weekly Engagement Trend</h3>
          <div className="parent-activity__chart-container" role="img" aria-label="Area chart showing weekly study minutes trend">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={currentActivity.weeklyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area type="monotone" dataKey="minutes" stroke="#4f6ef7" fill="rgba(79,110,247,0.1)" strokeWidth={2} />
                <Line type="monotone" dataKey="assignments" stroke="#22c55e" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricBox({ label, value, unit }) {
  return (
    <div className="parent-activity__metric-box">
      <span className="parent-activity__metric-value">{value}{unit && <small>{unit}</small>}</span>
      <span className="parent-activity__metric-label">{label}</span>
    </div>
  );
}

export default ParentActivity;
